import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ClipboardCheck, PhoneCall, Send, FileSignature, UserPlus, Mic, AlertTriangle, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { kpiAPI, teamAPI } from '@/lib/api';

/* Arbeitsbericht (Inhaber): was hat eine Mitarbeiterin heute / diese Woche
   erledigt — zusammengezogen aus Aufgaben, Follow-ups, Freigaben,
   Aktivitäten, Setter-Versuchen, Verträgen, Leads, Check-ins, Sprachnotizen
   und dem Audit-Log. Standard: Cagla, heute. */

const ZEITRAEUME = [[1, 'Heute'], [7, '7 Tage'], [30, '30 Tage']];
const ART_LABEL = { aufgabe: 'Aufgabe', follow_up: 'Follow-up', freigabe: 'Freigabe', aktivitaet: 'Aktivität', versuch: 'Setter', vertrag: 'Vertrag', lead: 'Lead', notiz: 'Notiz' };
const ART_CLS = { aufgabe: 'bg-primary/10 text-primary', follow_up: 'bg-amber-500/10 text-amber-700', freigabe: 'bg-emerald-500/10 text-emerald-700', aktivitaet: 'bg-muted text-muted-foreground', versuch: 'bg-violet-500/10 text-violet-700', vertrag: 'bg-emerald-600 text-white', lead: 'bg-sky-500/10 text-sky-700', notiz: 'bg-orange-500/10 text-orange-700' };

function zeit(iso) { const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
function euro(v) { return (v ?? 0).toLocaleString('de-DE', { maximumFractionDigits: 0 }) + ' €'; }

function Zahl({ icon: Icon, label, wert, zusatz, tid }) {
  return (
    <Card className="border-border/60" data-testid={tid}>
      <CardContent className="p-3 sm:p-4 flex items-start justify-between gap-2">
        <div><p className="text-[10px] sm:text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="text-xl sm:text-2xl font-bold tabular-nums">{wert}</p>
          {zusatz && <p className="text-[10px] text-muted-foreground">{zusatz}</p>}</div>
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-primary" /></div>
      </CardContent>
    </Card>
  );
}

export default function ArbeitsberichtPage() {
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  const [userId, setUserId] = useState('');
  const [tage, setTage] = useState(1);
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);

  useEffect(() => { teamAPI.list().then(r => setTeam(r.data?.users || r.data || [])).catch(() => {}); }, []);
  const lade = useCallback(async () => {
    setLaden(true);
    try { const r = await kpiAPI.arbeit(userId || undefined, tage); setDaten(r.data); if (!userId) setUserId(r.data.user.user_id); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Bericht konnte nicht geladen werden'); }
    finally { setLaden(false); }
  }, [userId, tage]);
  useEffect(() => { lade(); }, [lade]);

  const z = daten?.zahlen || {};
  const o = daten?.offen || {};
  const vorname = daten?.user?.name?.split(' ')[0] || '';

  return (
    <div className="space-y-6" data-testid="arbeitsbericht-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Arbeitsbericht{vorname ? ` · ${vorname}` : ''}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{daten ? `${daten.zeitraum.tage_liste[0].split('-').reverse().join('.')}${tage > 1 ? ` bis ${daten.zeitraum.tage_liste.at(-1).split('-').reverse().join('.')}` : ''} · ${z.gesamt} erfasste Handlungen` : 'Lädt …'}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <select value={userId} onChange={e => setUserId(e.target.value)} data-testid="arbeit-user" className="h-9 rounded-md border border-input bg-background px-3 text-sm">
            {!team.some(u => u.user_id === userId) && daten?.user && <option value={daten.user.user_id}>{daten.user.name}</option>}
            {team.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
          </select>
          {ZEITRAEUME.map(([t, l]) => <Button key={t} size="sm" variant={tage === t ? 'default' : 'outline'} onClick={() => setTage(t)}>{l}</Button>)}
          <Button size="sm" variant="outline" onClick={lade} disabled={laden} className="gap-1.5">{laden ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</Button>
        </div>
      </div>

      {daten && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
            <Zahl icon={ClipboardCheck} label="Aufgaben erledigt" wert={z.aufgaben_erledigt + z.follow_ups_erledigt} zusatz={`${z.onboarding_schritte} Onboarding · ${z.follow_ups_erledigt} Follow-ups`} tid="arbeit-aufgaben" />
            <Zahl icon={PhoneCall} label="Kontakte" wert={z.setter_versuche + z.aktivitaeten} zusatz={`${z.setter_erreicht} erreicht · ${z.anrufe} Anrufe`} tid="arbeit-kontakte" />
            <Zahl icon={Send} label="Freigaben" wert={z.freigaben_gesendet} zusatz={`${z.freigaben_verworfen} verworfen`} tid="arbeit-freigaben" />
            <Zahl icon={FileSignature} label="Verträge" wert={z.vertraege} zusatz={z.vertraege ? (z.vertragswert == null ? 'Vertragswert verborgen' : `Vertragswert ${euro(z.vertragswert)}`) : ''} tid="arbeit-vertraege" />
            <Zahl icon={UserPlus} label="Leads angelegt" wert={z.leads_angelegt} zusatz={`${z.checkins} Check-ins`} tid="arbeit-leads" />
            <Zahl icon={Mic} label="Notizen" wert={z.sprachnotizen} tid="arbeit-notizen" />
          </div>

          <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
            <div className="space-y-4">
              <Card className={`border-border/60 ${o.aufgaben_ueberfaellig || o.follow_ups_ueberfaellig ? 'border-red-300' : ''}`}>
                <CardContent className="p-4 space-y-2">
                  <h2 className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Offen bei {vorname || 'ihr'}</h2>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><p className="text-2xl font-bold tabular-nums">{o.aufgaben}</p><p className="text-xs text-muted-foreground">Aufgaben offen{o.aufgaben_ueberfaellig ? <span className="text-red-600 font-semibold"> · {o.aufgaben_ueberfaellig} überfällig</span> : ''}</p></div>
                    <div><p className="text-2xl font-bold tabular-nums">{o.follow_ups}</p><p className="text-xs text-muted-foreground">Follow-ups offen{o.follow_ups_ueberfaellig ? <span className="text-red-600 font-semibold"> · {o.follow_ups_ueberfaellig} überfällig</span> : ''}</p></div>
                  </div>
                </CardContent>
              </Card>

              {tage > 1 && (
                <Card className="border-border/60"><CardContent className="p-4">
                  <h2 className="text-sm font-semibold mb-2">Handlungen je Tag</h2>
                  <div style={{ width: '100%', height: 160 }}>
                    <ResponsiveContainer><BarChart data={daten.verlauf.map(v => ({ ...v, tag: v.tag.slice(8, 10) + '.' + v.tag.slice(5, 7) }))} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="tag" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip cursor={{ fill: 'hsl(var(--muted))' }} contentStyle={{ fontSize: 12 }} /><Bar dataKey="anzahl" name="Handlungen" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart></ResponsiveContainer>
                  </div>
                </CardContent></Card>
              )}

              {(Object.keys(z.aktivitaeten_nach_typ || {}).length > 0 || Object.keys(z.weitere_aktionen || {}).length > 0) && (
                <Card className="border-border/60"><CardContent className="p-4 space-y-1 text-sm">
                  <h2 className="text-sm font-semibold flex items-center gap-2 mb-1"><Activity className="w-4 h-4 text-primary" /> Im Detail</h2>
                  {Object.entries(z.aktivitaeten_nach_typ || {}).map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-medium tabular-nums">{v}</span></div>)}
                  {Object.entries(z.weitere_aktionen || {}).map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-medium tabular-nums">{v}</span></div>)}
                </CardContent></Card>
              )}
            </div>

            <Card className="border-border/60">
              <CardContent className="p-4">
                <h2 className="text-sm font-semibold mb-2">Was erledigt wurde <span className="text-xs font-normal text-muted-foreground">({daten.aktionen_gesamt})</span></h2>
                {daten.aktionen.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Im Zeitraum nichts erfasst.</p>}
                <ul className="divide-y divide-border/50">
                  {daten.aktionen.map((a, i) => (
                    <li key={i} className="py-2 flex items-start gap-2 text-sm">
                      <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap mt-0.5 w-[76px]">{zeit(a.zeit)}</span>
                      <Badge variant="outline" className={`text-[10px] border-0 ${ART_CLS[a.art] || ''}`}>{ART_LABEL[a.art] || a.art}</Badge>
                      <span className="flex-1 min-w-0 break-words">{a.titel}{a.lead_id && a.lead_name && !a.titel.includes(a.lead_name) ? <span className="text-muted-foreground"> · {a.lead_name}</span> : null}
                        {a.lead_id && <button className="ml-1 text-primary text-xs hover:underline" onClick={() => navigate(`/leads/${a.lead_id}`)}>öffnen</button>}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
